import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Typography,
} from '@mui/material';

const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const PAN_LIMIT = 100;
const MAX_OUTPUT_LONG_SIDE = 1920;

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Không đọc được ảnh'));
    img.src = src;
  });
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Không đọc được file'));
    reader.readAsDataURL(file);
  });
}

function resolveOutputSize(slotW: number, slotH: number): { width: number; height: number } {
  const w = Math.max(1, Math.round(slotW));
  const h = Math.max(1, Math.round(slotH));
  const long = Math.max(w, h);
  if (long <= MAX_OUTPUT_LONG_SIDE) return { width: w, height: h };
  const scale = MAX_OUTPUT_LONG_SIDE / long;
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  cw: number,
  ch: number,
  zoom: number,
  panX: number,
  panY: number
) {
  const baseScale = Math.max(cw / image.naturalWidth, ch / image.naturalHeight);
  const scale = baseScale * Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom));
  const drawW = image.naturalWidth * scale;
  const drawH = image.naturalHeight * scale;
  const extraX = Math.max(0, drawW - cw);
  const extraY = Math.max(0, drawH - ch);
  const safePanX = Math.max(-PAN_LIMIT, Math.min(PAN_LIMIT, panX));
  const safePanY = Math.max(-PAN_LIMIT, Math.min(PAN_LIMIT, panY));
  const dx = (cw - drawW) / 2 - (safePanX / 100) * (extraX / 2);
  const dy = (ch - drawH) / 2 - (safePanY / 100) * (extraY / 2);
  ctx.clearRect(0, 0, cw, ch);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, dx, dy, drawW, drawH);
}

export interface SlotAspectCropDialogProps {
  open: boolean;
  sourceFile: File | null;
  /** Kích thước ô slot đầu (pixel layout frame) — dùng làm tỉ lệ crop. */
  slotWidth: number;
  slotHeight: number;
  title?: string;
  hint?: string;
  confirmLabel?: string;
  onClose: () => void;
  onConfirm: (croppedFile: File) => void | Promise<void>;
}

/**
 * Dialog căn chỉnh/zoom ảnh theo tỉ lệ ô slot (cover mode) — giống mobile upload / album crop.
 */
const SlotAspectCropDialog: React.FC<SlotAspectCropDialogProps> = ({
  open,
  sourceFile,
  slotWidth,
  slotHeight,
  title = 'Căn chỉnh ảnh nền theo ô slot',
  hint = 'Kéo để dịch ảnh, dùng zoom để phóng to. Ảnh sẽ được cắt đúng tỉ lệ ô slot đầu của khung.',
  confirmLabel = 'Cắt & upload',
  onClose,
  onConfirm,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [src, setSrc] = useState('');
  const [fileName, setFileName] = useState('background.jpg');
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [applying, setApplying] = useState(false);
  const pointerRef = useRef<{
    pointerId: number | null;
    startX: number;
    startY: number;
    startOffsetX: number;
    startOffsetY: number;
  }>({ pointerId: null, startX: 0, startY: 0, startOffsetX: 0, startOffsetY: 0 });

  const outputSize = useMemo(
    () => resolveOutputSize(slotWidth || 1, slotHeight || 1),
    [slotWidth, slotHeight]
  );

  const aspectLabel = useMemo(() => {
    const w = Math.max(1, Math.round(slotWidth || 1));
    const h = Math.max(1, Math.round(slotHeight || 1));
    return `${w} × ${h}`;
  }, [slotWidth, slotHeight]);

  useEffect(() => {
    if (!open || !sourceFile) {
      setSrc('');
      return;
    }
    let cancelled = false;
    setZoom(1);
    setPanX(0);
    setPanY(0);
    setFileName(sourceFile.name?.replace(/\.[^.]+$/, '') + '.jpg' || 'background.jpg');
    fileToDataUrl(sourceFile)
      .then((dataUrl) => {
        if (!cancelled) setSrc(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc('');
      });
    return () => {
      cancelled = true;
    };
  }, [open, sourceFile]);

  const redraw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !src) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const image = await loadImageElement(src);
    canvas.width = outputSize.width;
    canvas.height = outputSize.height;
    drawCover(ctx, image, outputSize.width, outputSize.height, zoom, panX, panY);
  }, [src, outputSize.width, outputSize.height, zoom, panX, panY]);

  useEffect(() => {
    if (!open || !src) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const run = async (attempt = 0) => {
      if (cancelled) return;
      if (!canvasRef.current) {
        if (attempt < 12) timer = setTimeout(() => run(attempt + 1), 35);
        return;
      }
      try {
        await redraw();
      } catch {
        /* ignore preview errors */
      }
    };
    run();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, src, redraw]);

  const handleClose = () => {
    if (applying) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!src || applying) return;
    try {
      setApplying(true);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Không thể xử lý ảnh');
      const image = await loadImageElement(src);
      canvas.width = outputSize.width;
      canvas.height = outputSize.height;
      drawCover(ctx, image, outputSize.width, outputSize.height, zoom, panX, panY);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.92)
      );
      if (!blob) throw new Error('Không thể tạo ảnh sau khi cắt');
      const cropped = new File([blob], fileName || 'background.jpg', { type: 'image/jpeg' });
      await onConfirm(cropped);
    } finally {
      setApplying(false);
    }
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (applying) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    pointerRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startOffsetX: panX,
      startOffsetY: panY,
    };
    setDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = pointerRef.current;
    if (!dragging || state.pointerId !== event.pointerId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const deltaXPercent = rect.width > 0 ? ((event.clientX - state.startX) / rect.width) * 100 : 0;
    const deltaYPercent = rect.height > 0 ? ((event.clientY - state.startY) / rect.height) * 100 : 0;
    setPanX(Math.max(-PAN_LIMIT, Math.min(PAN_LIMIT, state.startOffsetX - deltaXPercent)));
    setPanY(Math.max(-PAN_LIMIT, Math.min(PAN_LIMIT, state.startOffsetY - deltaYPercent)));
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerRef.current.pointerId === event.pointerId) {
      pointerRef.current.pointerId = null;
      setDragging(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { width: '92vw', maxWidth: 920 } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {hint}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Tỉ lệ ô slot đầu: {aspectLabel} px → xuất {outputSize.width} × {outputSize.height}
        </Typography>

        <Box
          sx={{
            width: '100%',
            border: '1px solid #e5e7eb',
            borderRadius: 1.5,
            p: 1,
            bgcolor: '#f8fafc',
            display: 'flex',
            justifyContent: 'center',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <canvas
            ref={canvasRef}
            style={{
              width: 'min(100%, 420px)',
              aspectRatio: `${Math.max(1, slotWidth)} / ${Math.max(1, slotHeight)}`,
              display: 'block',
              borderRadius: 8,
              border: '1px solid #dbe4f0',
              backgroundColor: '#fff',
              touchAction: 'none',
              cursor: dragging ? 'grabbing' : 'grab',
            }}
          />
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <Typography variant="caption" color="text.secondary">
            Zoom
          </Typography>
          <Slider
            value={zoom}
            min={ZOOM_MIN}
            max={ZOOM_MAX}
            step={0.05}
            onChange={(_, value) => setZoom(Number(value))}
            disabled={applying || !src}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={applying}>
          Hủy
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={applying || !src}>
          {applying ? 'Đang xử lý...' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SlotAspectCropDialog;
